import type { BuilderProducts } from "api-types/builders/builderTypes";
import type { ClientBuilderUserPermission } from "api-types/builderUsers/builderUsersBaseTypes";
import {
  clientBuilderUserPermissionGroups,
  permissionHelpDocs,
  permissionDisplayNames,
  type UserManagementUIVisibilitySettings,
} from "api-types/builderUsers/builderUsersBaseTypes";
import type { FeatureSet } from "common/constants/features";

/**
 * Permission groups visible in the user management UI.
 * Visibility can be controlled per group and per permission, either by a
 * static boolean or by a feature flag key.
 */
function shouldShowInUserManagementUI(
  settings: UserManagementUIVisibilitySettings,
  enabledFeatures: FeatureSet,
  enabledProducts?: BuilderProducts,
): boolean {
  if (settings.showInUserManagementUI === false) {
    return false;
  }

  if (settings.gateBehindFeatureFlag) {
    if (!enabledFeatures[settings.gateBehindFeatureFlag]) {
      return false;
    }
  }

  if (settings.gateBehindProduct) {
    if (!enabledProducts?.[settings.gateBehindProduct]) {
      return false;
    }
  }

  return true;
}

type PermissionEntry = string | ({ permission: string } & UserManagementUIVisibilitySettings);

function getPermissionCode(permission: PermissionEntry): string {
  if (typeof permission === "string") {
    return permission;
  }

  return permission.permission;
}

function getPermissionVisibilitySettings(permission: PermissionEntry): UserManagementUIVisibilitySettings {
  if (typeof permission === "string") {
    return {};
  }

  return {
    showInUserManagementUI: permission.showInUserManagementUI,
    gateBehindFeatureFlag: permission.gateBehindFeatureFlag,
    gateBehindProduct: permission.gateBehindProduct,
  };
}

export function getVisiblePermissionGroups(
  enabledFeatures: FeatureSet,
  enabledProducts?: BuilderProducts,
): { group: string; permissions: ClientBuilderUserPermission[] }[] {
  return clientBuilderUserPermissionGroups
    .filter((group) => {
      const groupVisibilitySettings: UserManagementUIVisibilitySettings = {
        showInUserManagementUI: "showInUserManagementUI" in group ? group.showInUserManagementUI : undefined,
        gateBehindFeatureFlag: "gateBehindFeatureFlag" in group ? group.gateBehindFeatureFlag : undefined,
        gateBehindProduct: "gateBehindProduct" in group ? group.gateBehindProduct : undefined,
      };
      return shouldShowInUserManagementUI(groupVisibilitySettings, enabledFeatures, enabledProducts);
    })
    .map((group) => {
      const permissions = group.permissions
        .filter((permission) =>
          shouldShowInUserManagementUI(getPermissionVisibilitySettings(permission), enabledFeatures, enabledProducts),
        )
        .map((permission) => getPermissionCode(permission) as ClientBuilderUserPermission);

      return {
        group: group.group,
        permissions,
      };
    })
    .filter((group) => group.permissions.length > 0);
}

/**
 * Formats a permission code into a human-readable display name.
 * Uses custom display names from permissionDisplayNames if defined,
 * otherwise auto-generates from the permission code (e.g., VIEW_BUILDER_INFO -> "View Builder Info").
 */
export function formatPermissionName(permission: ClientBuilderUserPermission): string {
  const displayName = permissionDisplayNames[permission];
  if (displayName) {
    return displayName;
  }
  return permission
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getPermissionHelpDoc(permission: ClientBuilderUserPermission): string | undefined {
  return permissionHelpDocs[permission];
}
